// import useDeepCompareMemoize from "./useDeepCompareMemoize.js";
import * as R from "ramda";
import { useEffect, useReducer, useRef } from "react";
import { bool, shape, string } from "prop-types";

const CACHE_ENABLED = false;

if (!CACHE_ENABLED) {
  sessionStorage.clear();
  // console.warn("clear sessionStorage!");
}

export const remoteDataShape = (dataShape) =>
  shape({
    isLoading: bool,
    error: string,
    data: dataShape,
  });

const blankState = {
  isLoading: true,
  error: undefined,
  data: undefined,
};

const dataFetchReducer = (state, action) => {
  switch (action.type) {
    case "FETCH_RESET":
      return blankState;
    case "FETCH_INIT":
      return {
        ...state,
        isLoading: true,
        error: undefined,
      };
    case "FETCH_SUCCESS":
      return {
        ...state,
        isLoading: false,
        error: undefined,
        data: action.data,
      };
    case "FETCH_FAILURE":
      return {
        ...state,
        isLoading: false,
        error: action.error,
      };
    default:
      throw new Error();
  }
};

/**
 * useFetchJson
 * usage: const dataResponse = useFetchJson("/api?arg=1", { method: "GET" });
 *
 * @param  {[type]} args args to be pased to fetch, fetch(url, options)
 * https://developer.mozilla.org/en-US/docs/Web/API/WindowOrWorkerGlobalScope/fetch#Syntax
 * @return {Object} fetch state: { isLoading, error, data }
 *
 * NOTE options will change every render in the useage example above, so we need
 * to use useDeepCompareMemoize to make sure we don't unnecessarily fetch again
 * see https://github.com/kentcdodds/use-deep-compare-effect
 */
export default function useFetchJson(url, options) {
  // we can only cache if this is GET request (maybe in the future we can cache
  // for POST too, but we'll have to use the body as a cache key, etc)
  const shouldCache = CACHE_ENABLED && R.propEq("method", "GET", options || {});

  const cachedData = shouldCache && JSON.parse(sessionStorage.getItem(url));

  const initalState = shouldCache
    ? { ...blankState, data: cachedData }
    : blankState;

  const [state, dispatch] = useReducer(dataFetchReducer, initalState);

  // will only update if the options _value_ changes (not reference)
  const fetchOptions = useDeepCompareMemoize({ ...options });

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    let cancelled = false;

    // if there is no url, reset
    if (!url) {
      dispatch({
        type: "FETCH_RESET",
      });
      return;
    }

    dispatch({
      type: "FETCH_INIT",
    });

    const cachedData = shouldCache && JSON.parse(sessionStorage.getItem(url));

    // only fetch if we haven't fetched and cached already
    if (cachedData) {
      dispatch({
        type: "FETCH_SUCCESS",
        data: cachedData,
      });
    } else {
      fetch(url, { ...fetchOptions, ...signal })
        .then((response) => response.json())
        .then((data) => {
          if (!cancelled) {
            if (shouldCache) {
              sessionStorage.setItem(url, JSON.stringify(data));
            }

            dispatch({
              type: "FETCH_SUCCESS",
              data,
            });
          }
        })
        .catch((error) => {
          if (!cancelled) {
            dispatch({
              type: "FETCH_FAILURE",
              error: error.message,
            });
          }
        });
    }
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [fetchOptions, shouldCache, url]);

  return state;
}

// will only update if the value has changed with a deep compare (instead of by reference equality)
// https://github.com/kentcdodds/use-deep-compare-effect/blob/master/src/index.js#L21-L29
function useDeepCompareMemoize(value) {
  const ref = useRef();

  if (!R.equals(value, ref.current)) {
    ref.current = value;
  }

  return ref.current;
};
